// classicToolFloat — Classic サイドバー全体 (= 旧 Java 版 `paintToolFrame` 相当) の
// detach / 自由配置 pure helper。
//
// ## §12 方針変更 (2026-08-01、consult 後): サイドバー全体が「一体で 1 枚」浮く
//
// 当初 (S3 スライス2) は PalettePanel 1 枚だけを detach 対象にしていたが、
// 旧 Java 版ソース (`OekakichatClientPanel.openToolFrame()`) を確認した結果、
// **`/open tool` は `paintToolCanvas` (≒ 新版 ToolPanel/PalettePanel) +
// `colorMakeCanvas` (≒ ArgbSliderPanel) + `maskSetCanvas` (≒ MaskPanel) を
// 縦に並べたまま一体で 1 枚の別ウィンドウへ移す** ものだったと判明した (パネル単位で
// バラバラに浮くのではない)。本ファイルはその「1 枚」の detach 状態を表す最小限の
// pure state を持つ (= Classic 左サイドバー = `<fieldset>`[PalettePanel + ToolPanel +
// PenPressureConfig] + ArgbSliderPanel + MaskPanel、= `leftColRef` の中身全部)。
//
// `ToolPanel` を含めてサイドバー全体を対象にするため、**「ToolPanel (chrome
// 凍結) は固定」という当初方針は Classic では撤回**した (§12 確定、ユーザー判断)。
//
// ## detach 導線 = ドラッグ + `/open tool` の両方 (§12 確定)
//
// サイドバー内の 16 色グリッドはボタンが隙間なく敷き詰められているが、
// `usePanelDragGrip` + `NO_SHELL_DRAG_SELECTOR` (= ボタン/入力/canvas 等の
// インタラクティブ要素だけを除外し、それ以外はどこを掴んでもよい、
// `FloatShell.tsx` と同じ判定) をサイドバー **wrapper 全体** に適用することで、
// **新しい可視 chrome (グリップ帯やボタン) を追加せずに** ドラッグでの detach を
// 実現する (= §1「既定は現行配置のまま、触らなければ 1px も変わらない」を保ったまま
// ドラッグ導線を足せる)。`/open tool` コマンドは同じ状態を「即座に」切り替える
// 第二の導線 (旧 Java 版パリティの明示的な回復)。
//
// ## 詰める vs 空ける (§3 要検証 TODO への回答)
//
// フロート化したサイドバーは `leftColRef` の**同じ DOM ノード**を docked (grid
// セル) と floating (position:fixed) の両方で使い回し (= remount しない、
// pointer capture を跨いで維持するため)、docked 側には何も残らない。グリッドの
// 列テンプレート自体も detach 時は 2 列 (canvas + member) へ動的に切り替えるため、
// canvas が自動的に広がる (= 詰める。§3 の既定ルールと同じ結論)。

/** フロート位置 (px, viewport 座標系)。`floatShellGeometry.FloatPoint` と同形。 */
export interface ClassicToolFloatPosition {
  readonly x: number;
  readonly y: number;
}

/** Minimal Storage shape (`layerPopup.ts` の `StorageLike` と同流儀、循環 import を避けるため再定義)。 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * サイドバー全体のおおよその幅/高さ (= デフォルト位置算出 + 初回 clamp fallback 用。
 * 実寸は CSS/実測が決める、この定数は mount 直後 1 フレームだけの近似)。
 * §12 で対象が PalettePanel 単体からサイドバー全体 (パレット+ツール+筆圧 +
 * ARGB + マスク) に広がったため、概算値もそれに合わせて拡大した。
 */
export const TOOL_FLOAT_NOMINAL_WIDTH = 96;
export const TOOL_FLOAT_NOMINAL_HEIGHT = 560;

/** デフォルト位置を出す際の画面端マージン (px、LayerPopup/PenPopup と揃える)。 */
export const TOOL_FLOAT_MARGIN = 16;

/**
 * `/open tool` 実行時のデフォルト位置 = 画面左上寄り (旧ドック位置に近い場所、
 * 「浮いた実感」を出しつつ迷子にならない位置)。`innerWidth`/`innerHeight` は
 * node test で window 非依存に検証できるよう引数で受ける。
 */
export const defaultToolFloatPosition = (
  innerWidth: number,
  innerHeight: number,
): ClassicToolFloatPosition => {
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight)) {
    return { x: TOOL_FLOAT_MARGIN, y: TOOL_FLOAT_MARGIN };
  }
  return { x: TOOL_FLOAT_MARGIN + 96, y: TOOL_FLOAT_MARGIN + 48 };
};

/** localStorage キー: フロート位置 (per-user 記憶、LayerPopup/PenPopup と同流儀)。 */
export const TOOL_FLOAT_POS_KEY = 'oekaki:classic-tool-float-pos';

/**
 * 保存済み position を読み出す。不在 / 不正 JSON / storage 不可は `null`
 * (= caller がデフォルト位置に倒す)。例外は throw しない。
 */
export const readToolFloatPosition = (storage: StorageLike): ClassicToolFloatPosition | null => {
  try {
    const raw = storage.getItem(TOOL_FLOAT_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  } catch {
    return null;
  }
};

/** position を保存。storage 不可 / quota 超過は silently drop。 */
export const writeToolFloatPosition = (
  storage: StorageLike,
  pos: ClassicToolFloatPosition,
): void => {
  try {
    storage.setItem(TOOL_FLOAT_POS_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
  } catch {
    // 永続化できないだけで動作は継続させる。
  }
};

// ─── フロート open/closed (= docked/floating) state の永続化 ──────────────────
//
// LayerPopup/PenPopup の可視 state 永続化と同設計。既定は **false (docked)**
// (= §1「既定は現行配置のまま」、LayerPopup/PenPopup の「default open」とは逆で
// 「default docked」がここでの既定不変の意味)。

/** localStorage キー: フロート化しているか (per-user 記憶)。 */
export const TOOL_FLOAT_OPEN_KEY = 'oekaki:classic-tool-float-open';

/** 既定は docked (= フロートしていない)。§1 既定不変の要件。 */
export const DEFAULT_TOOL_FLOAT_OPEN = false;

export interface ToolFloatOpenStore {
  getInitial(): boolean;
  set(next: boolean): void;
}

export interface CreateToolFloatOpenStoreOptions {
  readonly storage?: StorageLike | null;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:tool-float-open__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export const createToolFloatOpenStore = (
  opts: CreateToolFloatOpenStoreOptions = {},
): ToolFloatOpenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  return {
    getInitial(): boolean {
      if (storage === null) return DEFAULT_TOOL_FLOAT_OPEN;
      try {
        const raw = storage.getItem(TOOL_FLOAT_OPEN_KEY);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return DEFAULT_TOOL_FLOAT_OPEN;
      } catch {
        return DEFAULT_TOOL_FLOAT_OPEN;
      }
    },
    set(next: boolean): void {
      if (storage === null) return;
      try {
        storage.setItem(TOOL_FLOAT_OPEN_KEY, next ? 'true' : 'false');
      } catch {
        // 永続化できないだけで動作は継続させる。
      }
    },
  };
};

// ─── ドック側 (左/右) の永続化 (2026-08-01 追加依頼: 右ドック対応) ──────────────
//
// Classic は Modern の 4-slot leftRail と違い、サイドバーの居場所は「左 or 右」の
// 2 択で十分 (grid が 3 列 [sidebar, canvas, member] しか持たず、sidebar が
// 最初か最後の列を取るかだけの違いのため)。既定は **'left'** (§1 既定不変)。
// 判定ロジック自体は増やさず、既存の `resolveEdgeSnapSide`/`EDGE_SNAP_THRESHOLD_PX`
// (panelDragGeometry.ts) をそのまま呼び出し側 (ClassicLayout.tsx) で使い回す
// (= 本ファイルは「選んだ side を覚えておく」永続化 store のみを持つ)。

/** サイドバーのドック側。既定 (§1) は `'left'`。 */
export type SidebarDockSide = 'left' | 'right';

/** localStorage キー: サイドバーのドック側 (per-user 記憶)。 */
export const SIDEBAR_DOCK_SIDE_KEY = 'oekaki:classic-sidebar-dock-side';

/** 既定は左ドック (= §1 既定不変の要件、現行配置と同じ)。 */
export const DEFAULT_SIDEBAR_DOCK_SIDE: SidebarDockSide = 'left';

export interface SidebarDockSideStore {
  getInitial(): SidebarDockSide;
  set(next: SidebarDockSide): void;
}

export interface CreateSidebarDockSideStoreOptions {
  readonly storage?: StorageLike | null;
}

export const createSidebarDockSideStore = (
  opts: CreateSidebarDockSideStoreOptions = {},
): SidebarDockSideStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  return {
    getInitial(): SidebarDockSide {
      if (storage === null) return DEFAULT_SIDEBAR_DOCK_SIDE;
      try {
        const raw = storage.getItem(SIDEBAR_DOCK_SIDE_KEY);
        if (raw === 'left' || raw === 'right') return raw;
        return DEFAULT_SIDEBAR_DOCK_SIDE;
      } catch {
        return DEFAULT_SIDEBAR_DOCK_SIDE;
      }
    },
    set(next: SidebarDockSide): void {
      if (storage === null) return;
      try {
        storage.setItem(SIDEBAR_DOCK_SIDE_KEY, next);
      } catch {
        // 永続化できないだけで動作は継続させる。
      }
    },
  };
};
